import { CrossMark } from '../components/icons.tsx';
import {
  SIM_CONSENT_TEXTS,
  SIM_LIMITS,
  SIM_NICKNAME_MESSAGES,
  simNicknameProblem,
  type SimMe,
} from '@cruz/shared';
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { navigate } from '../challenge/router.ts';
import { simApi, SimApiError, simErrorMessage } from './api.ts';
import {
  inputClass,
  Link,
  linkClass,
  Notice,
  PageTitle,
  primaryButton,
  secondaryButton,
} from './ui.tsx';

/** Registration, login and the pages the emails link to. */

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: (id: string, describedBy: string | undefined) => ReactNode;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-base font-semibold">
        {label}
      </label>
      {children(id, hint || error ? hintId : undefined)}
      {(error || hint) && (
        <p
          id={hintId}
          className={`mt-1.5 text-sm ${error ? 'font-medium text-burgundy dark:text-burgundy-300' : 'text-ink-soft dark:text-cream/70'}`}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

type Availability = { state: 'idle' | 'checking' | 'free' } | { state: 'bad'; message: string };

/** Live nickname check, 400 ms after the last key. */
function useNicknameCheck(nickname: string): Availability {
  const value = nickname.trim();
  const local = value ? simNicknameProblem(value) : null;
  const [remote, setRemote] = useState<{
    value: string;
    available: boolean;
    reason: string | null;
  } | null>(null);
  useEffect(() => {
    if (!value || local) return;
    let alive = true;
    const t = setTimeout(() => {
      simApi
        .nickname(value)
        .then((r) => alive && setRemote({ value, ...r }))
        .catch(() => {
          // Offline: the form checks again on submit
        });
    }, 400);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [value, local]);
  if (!value) return { state: 'idle' };
  if (local) return { state: 'bad', message: SIM_NICKNAME_MESSAGES[local] };
  if (remote?.value !== value) return { state: 'checking' };
  return remote.available
    ? { state: 'free' }
    : {
        state: 'bad',
        message:
          SIM_NICKNAME_MESSAGES[remote.reason as keyof typeof SIM_NICKNAME_MESSAGES] ??
          SIM_NICKNAME_MESSAGES.taken,
      };
}

function Checkbox({
  checked,
  onChange,
  children,
  required,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-purple/15 bg-paper px-4 py-3 dark:border-cream/15 dark:bg-night-800">
      <input
        type="checkbox"
        className="mt-1 h-5 w-5 flex-none accent-purple dark:accent-gold"
        checked={checked}
        required={required}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="text-[0.95rem] leading-snug">{children}</span>
    </label>
  );
}

export function RegisterScreen({ onDone }: { onDone: (me: SimMe) => void }) {
  const [nickname, setNickname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [terms, setTerms] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);
  const availability = useNicknameCheck(nickname);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!terms) return setError('Para jugar tienes que aceptar las condiciones.');
    setBusy(true);
    setError(null);
    setFieldError(null);
    try {
      const me = await simApi.register({
        nickname,
        email,
        password,
        accept_terms: true,
        marketing,
      });
      onDone(me);
    } catch (err) {
      const code = err instanceof SimApiError ? err.code : null;
      const message = simErrorMessage(err);
      if (code === 'nickname_taken' || code === 'nickname_invalid')
        setFieldError({ field: 'nickname', message });
      else if (code === 'email_taken') setFieldError({ field: 'email', message });
      else if (code === 'password_invalid') setFieldError({ field: 'password', message });
      else setError(message);
    } finally {
      setBusy(false);
    }
  };

  const nicknameHint =
    availability.state === 'checking'
      ? 'Comprobando…'
      : availability.state === 'free'
        ? '¡Libre! Así te verán los demás.'
        : `Entre ${SIM_LIMITS.nickname.min} y ${SIM_LIMITS.nickname.max} caracteres. Es lo único que se verá de ti.`;

  return (
    <div>
      <PageTitle kicker="Paso 1 de 2">Hazte hermano</PageTitle>
      <p className="mb-5 text-ink-soft dark:text-cream/80">
        Tu partida se guarda en tu cuenta: puedes seguir desde cualquier móvil.
      </p>
      <form className="space-y-5" onSubmit={(e) => void submit(e)} noValidate>
        <Field
          label="Apodo"
          hint={nicknameHint}
          error={
            fieldError?.field === 'nickname'
              ? fieldError.message
              : availability.state === 'bad'
                ? availability.message
                : null
          }
        >
          {(id, d) => (
            <input
              id={id}
              aria-describedby={d}
              className={inputClass}
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              autoComplete="nickname"
              maxLength={40}
              required
            />
          )}
        </Field>
        <Field
          label="Correo electrónico"
          hint="Para recuperar la contraseña. No se enseña a nadie."
          error={fieldError?.field === 'email' ? fieldError.message : null}
        >
          {(id, d) => (
            <input
              id={id}
              aria-describedby={d}
              className={inputClass}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </Field>
        <Field
          label="Contraseña"
          hint={`Al menos ${SIM_LIMITS.password.min} caracteres. Mejor una frase que solo sepas tú.`}
          error={fieldError?.field === 'password' ? fieldError.message : null}
        >
          {(id, d) => (
            <PasswordInput
              id={id}
              describedBy={d}
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
            />
          )}
        </Field>
        <div className="space-y-3">
          <Checkbox checked={terms} onChange={setTerms} required>
            He leído y acepto las <Link to="/simulador/condiciones">condiciones de uso</Link> y la{' '}
            <Link to="/simulador/privacidad">política de privacidad</Link>.
            <span className="sr-only"> ({SIM_CONSENT_TEXTS.terms})</span>
          </Checkbox>
          <Checkbox checked={marketing} onChange={setMarketing}>
            {SIM_CONSENT_TEXTS.marketing}{' '}
            <span className="text-ink-soft dark:text-cream/70">(Opcional)</span>
          </Checkbox>
        </div>
        {error && <Notice tone="error">{error}</Notice>}
        <button type="submit" className={primaryButton} disabled={busy}>
          {busy ? 'Inscribiéndote…' : 'Crear mi cuenta'}
        </button>
        <p className="text-center">
          ¿Ya tienes cuenta? <Link to="/simulador/entrar">Entra</Link>
        </p>
      </form>
    </div>
  );
}

function PasswordInput({
  id,
  describedBy,
  value,
  onChange,
  autoComplete,
}: {
  id: string;
  describedBy?: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: 'new-password' | 'current-password';
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        aria-describedby={describedBy}
        className={`${inputClass} pr-24`}
        type={show ? 'text' : 'password'}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={SIM_LIMITS.password.max}
        required
      />
      <button
        type="button"
        className="absolute inset-y-1 right-1 rounded-lg px-3 text-sm font-medium text-purple hover:bg-cream-200 dark:text-gold-300 dark:hover:bg-night-800"
        onClick={() => setShow(!show)}
        aria-pressed={show}
      >
        {show ? 'Ocultar' : 'Mostrar'}
      </button>
    </div>
  );
}

export function LoginScreen({ onDone }: { onDone: (me: SimMe) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onDone(await simApi.login(email, password));
    } catch (err) {
      setError(simErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageTitle>Entra en tu cuenta</PageTitle>
      <form className="space-y-5" onSubmit={(e) => void submit(e)}>
        <Field label="Correo electrónico">
          {(id) => (
            <input
              id={id}
              className={inputClass}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </Field>
        <Field label="Contraseña">
          {(id) => (
            <PasswordInput
              id={id}
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
            />
          )}
        </Field>
        {error && <Notice tone="error">{error}</Notice>}
        <button type="submit" className={primaryButton} disabled={busy}>
          {busy ? 'Entrando…' : 'Entrar'}
        </button>
        <p className="text-center">
          <Link to="/simulador/recuperar">¿Has olvidado la contraseña?</Link>
        </p>
        <p className="text-center">
          ¿Aún no juegas? <Link to="/simulador/registro">Crea tu cuenta</Link>
        </p>
      </form>
    </div>
  );
}

export function ForgotScreen() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await simApi.forgot(email);
      setSent(true);
    } catch (err) {
      setError(simErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-4">
        <PageTitle>Mira tu correo</PageTitle>
        <p>
          Si hay una cuenta con <strong>{email}</strong>, te hemos mandado un enlace para elegir una
          contraseña nueva. Sirve durante una hora.
        </p>
        <p className="text-ink-soft dark:text-cream/75">¿No llega? Mira en la carpeta de spam.</p>
        <Link to="/simulador/entrar" className={secondaryButton}>
          Volver a entrar
        </Link>
      </div>
    );
  }
  return (
    <div>
      <PageTitle>Recupera tu contraseña</PageTitle>
      <form className="space-y-5" onSubmit={(e) => void submit(e)}>
        <Field label="Correo electrónico de tu cuenta">
          {(id) => (
            <input
              id={id}
              className={inputClass}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </Field>
        {error && <Notice tone="error">{error}</Notice>}
        <button type="submit" className={primaryButton} disabled={busy}>
          {busy ? 'Enviando…' : 'Enviarme el enlace'}
        </button>
      </form>
    </div>
  );
}

export function ResetScreen({ token, onDone }: { token: string; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await simApi.reset(token, password);
      onDone();
    } catch (err) {
      setError(simErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <Notice tone="error">
        Este enlace no está completo. Pide otro desde «¿Has olvidado la contraseña?».
      </Notice>
    );
  }
  return (
    <div>
      <PageTitle>Elige una contraseña nueva</PageTitle>
      <form className="space-y-5" onSubmit={(e) => void submit(e)}>
        <Field label="Contraseña nueva" hint={`Al menos ${SIM_LIMITS.password.min} caracteres.`}>
          {(id, d) => (
            <PasswordInput
              id={id}
              describedBy={d}
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
            />
          )}
        </Field>
        {error && <Notice tone="error">{error}</Notice>}
        <button type="submit" className={primaryButton} disabled={busy}>
          {busy ? 'Guardando…' : 'Guardar y entrar'}
        </button>
        <p className="text-sm text-ink-soft dark:text-cream/70">
          Por seguridad, se cerrará la sesión en tus otros dispositivos.
        </p>
      </form>
    </div>
  );
}

/** Email links already followed on this page: each one is used once, even if React mounts twice. */
const followed = new Map<string, Promise<unknown>>();

/** Runs the action of an email link once, and says how it went. */
function useLinkAction(key: string, run: () => Promise<unknown>) {
  const [state, setState] = useState<'working' | 'ok' | { error: string }>(
    key ? 'working' : { error: 'Este enlace no está completo.' },
  );
  useEffect(() => {
    if (!key) return;
    let alive = true;
    if (!followed.has(key)) followed.set(key, run());
    followed
      .get(key)!
      .then(() => alive && setState('ok'))
      .catch((err: unknown) => alive && setState({ error: simErrorMessage(err) }));
    return () => {
      alive = false;
    };
    // Once per link
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state;
}

export function VerifyScreen({ token, signedIn }: { token: string; signedIn: boolean }) {
  const state = useLinkAction(token && `verify:${token}`, () => simApi.verify(token));
  return (
    <div className="space-y-4">
      <PageTitle>Verificar tu correo</PageTitle>
      {state === 'working' && <p>Comprobando el enlace…</p>}
      {state === 'ok' && <Notice>¡Hecho! Tu correo está verificado.</Notice>}
      {typeof state === 'object' && <Notice tone="error">{state.error}</Notice>}
      {state !== 'working' && (
        <Link to={signedIn ? '/simulador' : '/simulador/entrar'} className={primaryButton}>
          {signedIn ? 'Seguir jugando' : 'Entrar'}
        </Link>
      )}
    </div>
  );
}

export function UnsubscribeScreen({ unsubscribeKey }: { unsubscribeKey: string }) {
  const [state, setState] = useState<'ask' | 'working' | 'ok' | { error: string }>(
    unsubscribeKey ? 'ask' : { error: 'Este enlace no está completo.' },
  );
  const go = () => {
    setState('working');
    simApi
      .unsubscribe(unsubscribeKey)
      .then(() => setState('ok'))
      .catch((err: unknown) => setState({ error: simErrorMessage(err) }));
  };
  return (
    <div className="space-y-4">
      <PageTitle>Darte de baja</PageTitle>
      {(state === 'ask' || state === 'working') && (
        <>
          <p>¿Dejamos de mandarte novedades por correo? Seguirás pudiendo jugar igual.</p>
          <button
            type="button"
            className={primaryButton}
            onClick={go}
            disabled={state === 'working'}
          >
            Sí, no quiero más correos
          </button>
        </>
      )}
      {state === 'ok' && (
        <Notice>
          Listo: no te mandaremos más novedades. Puedes volver a apuntarte desde los ajustes de tu
          cuenta.
        </Notice>
      )}
      {typeof state === 'object' && <Notice tone="error">{state.error}</Notice>}
      <p>
        <Link to="/simulador">Ir al juego</Link>
      </p>
    </div>
  );
}

/** The start page for visitors: what the game is, and two buttons. */
/**
 * What a share card looks like, drawn in the page (the real ones are images made by the
 * server). An invented player and brotherhood.
 */
function ExampleCard() {
  return (
    <figure className="flex flex-col items-center gap-2">
      <div
        aria-hidden
        className="w-[15.5rem] -rotate-2 rounded-2xl border border-gold/70 bg-[radial-gradient(120%_70%_at_20%_0%,#6b3f78_0%,#4a1f55_45%,#2b1232_100%)] px-5 pt-4 pb-4 text-cream shadow-[0_12px_30px_rgba(43,18,50,0.35)]"
      >
        <p className="flex items-center gap-2 border-b border-gold/50 pb-2.5 text-[0.6rem] font-semibold tracking-[0.18em] text-gold-300 uppercase">
          <CrossMark size={16} />
          De cirio a hermano mayor
        </p>
        <p className="mt-4 text-[0.6rem] font-semibold tracking-[0.18em] text-gold-300 uppercase">
          Ascenso
        </p>
        <p className="mt-1 font-serif text-[1.9rem] leading-[1.02] font-semibold">Fiscal de paso</p>
        <p className="mt-2 text-[0.8rem] leading-snug text-cream/85">
          Me han nombrado fiscal de paso tras 18 años de hermano.
        </p>
        <div className="mt-5 rounded-lg border border-gold/60 bg-black/20 px-3 py-2.5">
          <p className="font-serif text-lg leading-tight font-semibold">Manolo de la Cera</p>
          <p className="text-[0.7rem] text-cream/75">La Paciencia</p>
          <p className="mt-1.5 border-t border-cream/15 pt-1.5 text-[0.7rem] font-semibold text-gold-300">
            Hermano nº 37 · 18 años
          </p>
        </div>
      </div>
      <figcaption className="text-sm text-ink-soft dark:text-cream/70">
        Así se ve un ascenso en tus estados.
      </figcaption>
    </figure>
  );
}

export function Landing() {
  return (
    <div className="space-y-6">
      <div className="pt-2">
        <p className="mb-2 text-xs font-semibold tracking-[0.16em] text-gold-700 uppercase dark:text-gold-300">
          Un juego para todo el año
        </p>
        <h1 className="font-serif text-[2.6rem] leading-[1.05] font-semibold text-purple dark:text-cream">
          De cirio a hermano mayor
        </h1>
        <p className="mt-4 text-lg leading-relaxed">
          Funda tu propia hermandad, ponle nombre, día e iglesia, e inscríbete como el último
          hermano. Empiezas de cirio en el último tramo… y quién sabe dónde acabas.
        </p>
      </div>
      <div className="space-y-3">
        <button
          type="button"
          className={primaryButton}
          onClick={() => navigate('/simulador/registro')}
        >
          Empezar mi carrera
        </button>
        <button
          type="button"
          className={secondaryButton}
          onClick={() => navigate('/simulador/entrar')}
        >
          Ya tengo cuenta
        </button>
      </div>
      <ExampleCard />
      <ul className="space-y-3">
        {[
          [
            'Cuatro decisiones al día',
            'Un rato cada día. Mañana sabrás qué pasó con lo que dejaste a medias.',
          ],
          [
            'Gente que no olvida',
            'La priostía, el capataz, tu abuela, el que se sabe todas las marchas… y tu rival.',
          ],
          [
            'Logros para presumir',
            'Cada ascenso y cada logro, en una tarjeta para tus estados de WhatsApp.',
          ],
        ].map(([title, text]) => (
          <li
            key={title}
            className="rounded-xl border border-purple/15 bg-paper px-4 py-3 dark:border-cream/15 dark:bg-night-800"
          >
            <p className="font-serif text-xl font-semibold text-purple dark:text-gold-300">
              {title}
            </p>
            <p className="text-ink-soft dark:text-cream/80">{text}</p>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className={primaryButton}
        onClick={() => navigate('/simulador/registro')}
      >
        Fundar mi hermandad
      </button>
      <p className="text-center text-sm text-ink-soft dark:text-cream/70">
        Nos reímos del mundo cofrade, nunca de la fe. Hermandades y personajes son inventados.
      </p>
      <p className="text-center text-sm">
        <Link to="/simulador/condiciones" className={linkClass}>
          Condiciones
        </Link>
        {' · '}
        <Link to="/simulador/privacidad" className={linkClass}>
          Privacidad
        </Link>
        {' · '}
        <a href="/" className={linkClass}>
          Cruz de IA
        </a>
      </p>
    </div>
  );
}
